import { useState } from 'react';
import { toast } from 'react-toastify';
import './MarketplacePage.css';

function MarketplacePage() {
    const [activeTab, setActiveTab] = useState<'profile' | 'discover' | 'passport'>('profile');
    const [isGeneratingStory, setIsGeneratingStory] = useState(false);

    const generateStory = async () => {
        setIsGeneratingStory(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            toast.success('Story generated! Review and edit before publishing.');
        } catch (error) {
            toast.error('Failed to generate story');
        } finally {
            setIsGeneratingStory(false);
        }
    };

    return (
        <div className="marketplace-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">🌍</span>
                Global Marketplace
            </h1>
            <p className="page-subtitle">
                Connect with buyers worldwide and tell your artisan story
            </p>

            <div className="tab-nav">
                <button
                    className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    👤 My Profile
                </button>
                <button
                    className={`tab-btn ${activeTab === 'discover' ? 'active' : ''}`}
                    onClick={() => setActiveTab('discover')}
                >
                    🔍 Discover
                </button>
                <button
                    className={`tab-btn ${activeTab === 'passport' ? 'active' : ''}`}
                    onClick={() => setActiveTab('passport')}
                >
                    📋 Digital Passport
                </button>
            </div>

            {activeTab === 'profile' && (
                <div className="profile-section animate-slide-up">
                    <div className="profile-header-card card">
                        <div className="profile-cover">
                            <div className="cover-placeholder">
                                <span>🏔️</span>
                                <button className="btn btn-secondary btn-sm">Change Cover</button>
                            </div>
                        </div>
                        <div className="profile-avatar">
                            <span>🎨</span>
                        </div>
                        <div className="profile-actions">
                            <span className="verification-badge pending">⏳ Pending Verification</span>
                            <button className="btn btn-secondary btn-sm">Edit Profile</button>
                        </div>
                    </div>

                    <div className="form-section card">
                        <h3>Basic Information</h3>
                        <div className="form-group">
                            <label>Display Name</label>
                            <input type="text" placeholder="Maria's Ceramics Studio" />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Country</label>
                                <select defaultValue="">
                                    <option value="">Select Country</option>
                                    <option value="ES">Spain</option>
                                    <option value="IT">Italy</option>
                                    <option value="PT">Portugal</option>
                                    <option value="FR">France</option>
                                    <option value="MX">Mexico</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Region</label>
                                <input type="text" placeholder="e.g., Andalusia" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Craft Types</label>
                            <div className="craft-tags">
                                <span className="craft-tag">🏺 Ceramics</span>
                                <span className="craft-tag">🎨 Pottery</span>
                                <button className="add-tag-btn">+ Add</button>
                            </div>
                        </div>
                    </div>

                    <div className="story-section card">
                        <div className="story-header">
                            <h3>Your Artisan Story</h3>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={generateStory}
                                disabled={isGeneratingStory}
                            >
                                {isGeneratingStory ? '✨ Generating...' : '✨ Generate with AI'}
                            </button>
                        </div>
                        <div className="form-group">
                            <label>Story Title</label>
                            <input type="text" placeholder="e.g., From Grandmother's Wheel to Global Markets" />
                        </div>
                        <div className="form-group">
                            <label>Your Story</label>
                            <textarea
                                rows={6}
                                placeholder="Share your journey as an artisan... What inspired you? What makes your craft unique? What traditions do you preserve?"
                            ></textarea>
                        </div>
                    </div>

                    <div className="wholesale-section card">
                        <h3>🏢 B2B / Wholesale Settings</h3>
                        <div className="toggle-row">
                            <div>
                                <strong>Enable Wholesale Inquiries</strong>
                                <p className="toggle-description">Allow businesses to request bulk orders</p>
                            </div>
                            <label className="toggle-switch">
                                <input type="checkbox" />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                        <div className="form-group">
                            <label>Minimum Order Value</label>
                            <div className="input-with-addon">
                                <span className="input-addon">€</span>
                                <input type="number" placeholder="500" />
                            </div>
                        </div>
                    </div>

                    <button className="btn btn-primary btn-large">
                        Save Profile
                    </button>
                </div>
            )}

            {activeTab === 'discover' && (
                <div className="discover-section animate-slide-up">
                    <div className="search-bar">
                        <input type="text" placeholder="Search artisans by craft, location, or name..." />
                        <button className="btn btn-primary">Search</button>
                    </div>

                    <div className="filter-row">
                        <select defaultValue="">
                            <option value="">All Crafts</option>
                            <option value="ceramics">Ceramics</option>
                            <option value="textiles">Textiles</option>
                            <option value="jewelry">Jewelry</option>
                            <option value="woodwork">Woodwork</option>
                        </select>
                        <select defaultValue="">
                            <option value="">All Countries</option>
                            <option value="ES">Spain</option>
                            <option value="IT">Italy</option>
                            <option value="MX">Mexico</option>
                        </select>
                        <label className="filter-checkbox">
                            <input type="checkbox" />
                            <span>Verified Only</span>
                        </label>
                        <label className="filter-checkbox">
                            <input type="checkbox" />
                            <span>Wholesale</span>
                        </label>
                    </div>

                    <div className="empty-state">
                        <span className="empty-icon">🌍</span>
                        <h3>Coming Soon</h3>
                        <p>The artisan marketplace is launching soon. Be among the first to join!</p>
                        <button className="btn btn-primary">Join Waitlist</button>
                    </div>
                </div>
            )}

            {activeTab === 'passport' && (
                <div className="passport-section animate-slide-up">
                    <div className="passport-intro card">
                        <div className="passport-icon">📋</div>
                        <h3>Digital Product Passports</h3>
                        <p>
                            Create EU DPP-compliant digital passports for your products. Each passport contains
                            material sourcing, sustainability data, and a unique QR code.
                        </p>
                    </div>

                    <div className="passport-benefits">
                        <div className="benefit-card">
                            <span className="benefit-icon">🌱</span>
                            <h4>Sustainability Score</h4>
                            <p>Showcase your eco-friendly practices</p>
                        </div>
                        <div className="benefit-card">
                            <span className="benefit-icon">📍</span>
                            <h4>Origin Tracking</h4>
                            <p>Trace materials and crafting location</p>
                        </div>
                        <div className="benefit-card">
                            <span className="benefit-icon">🔄</span>
                            <h4>Lifecycle Info</h4>
                            <p>Repair and recycling guidance</p>
                        </div>
                    </div>

                    <div className="card">
                        <h3>Create a Passport</h3>
                        <div className="form-group">
                            <label>Select Product</label>
                            <select defaultValue="">
                                <option value="">Choose a product...</option>
                            </select>
                        </div>
                        <button className="btn btn-primary">
                            📋 Generate Digital Passport
                        </button>
                    </div>

                    <div className="passports-list">
                        <h3>Your Passports</h3>
                        <div className="empty-state small">
                            <p>No passports created yet. Select a product to generate one.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MarketplacePage;
